import {
  InfoCircleOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import type { AgenticClientListItem } from "@p0security/integrations-agentic-shared/interface";
import { itemRouteSegment } from "@p0security/shared/install/routes";
import {
  Button,
  Empty,
  Flex,
  Input,
  Table,
  TableColumnsType,
  Tooltip,
  Typography,
} from "@p0security/ui";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { compact, get } from "lodash-es";
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AgenticClientsContext } from "../AgenticClientsContext";
import { ClientDetailsDrawer } from "../ClientDetailsDrawer";
import { ClientRegistrationModal } from "../ClientRegistrationModal";
import {
  defaultMcpClientName,
  formatMcpClientName,
  formatMcpClientPlatform,
  mcpClientPlatformIcon,
} from "../clientPlatforms";
import {
  ErrorAlert,
  matchesSearch,
  useAgenticContext,
} from "./AgenticOverviewContext";

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const formatRegistrationTime = (timestamp?: number) =>
  timestamp === undefined
    ? "Not recorded"
    : DATE_FORMAT.format(new Date(timestamp));

const isNestedInteractiveElement = (target: EventTarget) =>
  target instanceof Element && Boolean(target.closest("a, button"));

const CLIENT_COLUMNS: TableColumnsType<AgenticClientListItem> = [
  {
    key: "client",
    render: (_, client) => (
      <div className="flex min-w-0 flex-col">
        <Typography.Text strong>{formatMcpClientName(client)}</Typography.Text>
        <span className="text-xs leading-snug text-[var(--ant-color-text-secondary)]">
          {client.displayName
            ? defaultMcpClientName(client)
            : "Registered client"}
        </span>
      </div>
    ),
    title: "Client",
    width: 220,
  },
  {
    key: "platform",
    render: (_, client) => (
      <Flex align="center" gap={8}>
        {client.type === "jwt_bearer" ? (
          <>
            <SafetyCertificateOutlined aria-hidden="true" />
            <div className="flex min-w-0 flex-col">
              <Typography.Text>Federated</Typography.Text>
              <TenantAwareLink
                className="text-xs leading-snug"
                to={`integrations/identity-provider/provider/${itemRouteSegment(
                  client.providerId
                )}`}
              >
                {client.providerId}
              </TenantAwareLink>
            </div>
          </>
        ) : (
          <>
            {mcpClientPlatformIcon(client.platform)}
            <div className="flex min-w-0 flex-col">
              <Typography.Text>
                {formatMcpClientPlatform(client.platform)}
              </Typography.Text>
              <Typography.Text
                className="font-mono text-xs leading-snug"
                type="secondary"
              >
                {client.version}
              </Typography.Text>
            </div>
          </>
        )}
      </Flex>
    ),
    title: "Provider",
    width: 180,
  },
  {
    dataIndex: "id",
    key: "id",
    render: (id: string) => (
      <Typography.Text
        className="break-all font-mono text-xs"
        copyable={{ text: id }}
      >
        {id}
      </Typography.Text>
    ),
    title: "Client ID",
    width: 300,
  },
  {
    key: "owner",
    render: (_, client) =>
      client.owner ? (
        <Typography.Text>{client.owner}</Typography.Text>
      ) : (
        <Typography.Text type="secondary">No owner</Typography.Text>
      ),
    title: "Owner",
    width: 240,
  },
  {
    key: "registered",
    render: (_, client) => (
      <div className="flex min-w-0 flex-col">
        <Typography.Text>
          {get(client.createdBy, "user") ?? "Automated registration"}
        </Typography.Text>
        {client.createdAt !== undefined ? (
          <time
            className="text-xs leading-snug text-[var(--ant-color-text-secondary)]"
            dateTime={new Date(client.createdAt).toISOString()}
          >
            {formatRegistrationTime(client.createdAt)}
          </time>
        ) : (
          <span className="text-xs leading-snug text-[var(--ant-color-text-secondary)]">
            Not recorded
          </span>
        )}
      </div>
    ),
    title: (
      <Flex align="center" gap={6}>
        <span>Registered by</span>
        <Tooltip title="The P0 user who registered this client, not the OAuth user acting during a session.">
          <InfoCircleOutlined
            aria-label="About the registered by column"
            className="cursor-help text-[var(--ant-color-text-secondary)]"
            tabIndex={0}
          />
        </Tooltip>
      </Flex>
    ),
    width: 240,
  },
];

const ClientTable: React.FC<{
  clients: AgenticClientListItem[];
  emptyDescription: string;
  loading: boolean;
  onSelectClient: (client: AgenticClientListItem) => void;
}> = ({ clients, emptyDescription, loading, onSelectClient }) => {
  const clientRow = useCallback(
    (client: AgenticClientListItem) => ({
      "aria-haspopup": "dialog" as const,
      "aria-label": `View details for ${formatMcpClientName(client)}`,
      className: "cursor-pointer",
      onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
        if (isNestedInteractiveElement(event.target)) return;
        onSelectClient(client);
      },
      onKeyDown: (event: ReactKeyboardEvent<HTMLTableRowElement>) => {
        if (
          event.target !== event.currentTarget ||
          (event.key !== "Enter" && event.key !== " ")
        ) {
          return;
        }
        event.preventDefault();
        onSelectClient(client);
      },
      role: "button",
      tabIndex: 0,
    }),
    [onSelectClient]
  );

  return (
    <div aria-label={loading ? "Loading clients" : undefined}>
      <Table<AgenticClientListItem>
        className="[&_.ant-table-tbody>tr>td]:align-middle"
        columns={CLIENT_COLUMNS}
        dataSource={clients}
        loading={loading}
        locale={{
          emptyText: (
            <Empty
              description={emptyDescription}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        pagination={{ hideOnSinglePage: true, pageSize: 10 }}
        onRow={clientRow}
        rowKey="id"
        scroll={{ x: 1180 }}
      />
    </div>
  );
};

/** Clients tab. A `:clientId` segment opens that client's drawer — the same way
 * access management opens a request from `history/:requestId`. */
export const AgenticClients: React.FC = () => {
  const {
    clients,
    error: clientsError,
    loading: clientsLoading,
    refresh: refreshClients,
  } = useContext(AgenticClientsContext);
  const {
    canManageServers,
    canRegisterClients,
    clientUpdateError,
    isRegistering,
    isUpdatingClient,
    onClearClientUpdateError,
    onClearRegistrationError,
    onRegister,
    onUpdateClient,
    registrationError,
  } = useAgenticContext();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [clientSearch, setClientSearch] = useState("");
  const [registrationOpen, setRegistrationOpen] = useState(false);

  // Client IDs are table keys, so normalize duplicated legacy payloads first.
  const uniqueClients = useMemo(
    () => [...new Map(clients.map((client) => [client.id, client])).values()],
    [clients]
  );
  const selectedClient = uniqueClients.find((client) => client.id === clientId);

  const filteredClients = useMemo(
    () =>
      uniqueClients.filter((client) =>
        matchesSearch(
          clientSearch,
          compact([
            formatMcpClientName(client),
            defaultMcpClientName(client),
            client.type === "jwt_bearer"
              ? "Federated"
              : formatMcpClientPlatform(client.platform),
            client.id,
            client.type === "jwt_bearer" ? client.providerId : client.version,
            client.type !== "jwt_bearer" ? client.createdBy?.user : null,
            client.owner,
            formatRegistrationTime(client.createdAt),
          ])
        )
      ),
    [clientSearch, uniqueClients]
  );

  const updateClientSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      setClientSearch(event.target.value),
    []
  );

  const openRegistration = useCallback(() => {
    onClearRegistrationError();
    setRegistrationOpen(true);
  }, [onClearRegistrationError]);

  const closeRegistration = useCallback(() => setRegistrationOpen(false), []);

  const selectClient = useCallback(
    (client: AgenticClientListItem) =>
      navigate({ pathname: encodeURIComponent(client.id) }),
    [navigate]
  );

  const closeClientDetails = useCallback(() => {
    onClearClientUpdateError();
    navigate({ pathname: ".." }, { relative: "path" });
  }, [navigate, onClearClientUpdateError]);

  return (
    <section aria-label="Clients">
      {canRegisterClients && (
        <ClientRegistrationModal
          clearError={onClearRegistrationError}
          error={registrationError}
          loading={isRegistering}
          onClose={closeRegistration}
          onRegister={onRegister}
          open={registrationOpen}
        />
      )}
      <Flex gap={16} vertical>
        <Flex align="center" gap={12} justify="space-between" wrap>
          <div className="w-full max-w-xl">
            <Input.Search
              allowClear
              aria-label="Search clients"
              className="w-full"
              placeholder="Search clients"
              value={clientSearch}
              onChange={updateClientSearch}
            />
          </div>
          {canRegisterClients && (
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={openRegistration}
            >
              Register client
            </Button>
          )}
        </Flex>
        {clientsError ? (
          <ErrorAlert messageText={clientsError} onRetry={refreshClients} />
        ) : (
          <ClientTable
            clients={filteredClients}
            emptyDescription={
              clientSearch.trim()
                ? "No clients match your search."
                : "No clients have been registered yet."
            }
            loading={clientsLoading}
            onSelectClient={selectClient}
          />
        )}
      </Flex>
      <ClientDetailsDrawer
        canManageServers={canManageServers}
        canRegisterOauth={canRegisterClients}
        client={selectedClient}
        error={clientUpdateError}
        loading={isUpdatingClient}
        onClose={closeClientDetails}
        onUpdate={onUpdateClient}
      />
    </section>
  );
};
